// src/hooks/canvas/useDxfScene.js
//
// DXF entity yuklendiginde sahneye cizim + ilk viewport fit.

import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as THREE from 'three';
import { selectDxfEntities, selectDxfBlocks } from '../../../modules/dxf';
import { setLoadingMessage } from '../../../redux/dxfSlice';
import { dxfDrawCore } from '../../../modules/dxf';
import { computeSafeSceneBox } from '../../../modules/fpSync';

export default function useDxfScene(threeState, scenesRef, controlsRef, canvasRef, onSceneReady) {
  const dxfEntities = useSelector(selectDxfEntities);
  const dxfBlocks = useSelector(selectDxfBlocks);
  const allLayers = useSelector(state => state.layer.allLayers || []);
  const dxfLoadId = useSelector(state => state.dxf.loadId);
  const projectIsLoading = useSelector(state => state.project?.isLoading);
  const alreadyDrawn = useRef(false);
  const prevDxfLoadIdRef = useRef(0);
  const prevProjectLoadingRef = useRef(false);
  const dispatch = useDispatch();

  // DXF data LOAD change detection — force scene rebuild
  useEffect(() => {
    if (dxfLoadId !== prevDxfLoadIdRef.current) {
      if (prevDxfLoadIdRef.current !== 0 && alreadyDrawn.current) {
        if (threeState?.scene) {
          const toRemove = [];
          threeState.scene.children.forEach(child => {
            // Yeni: isDxfRoot group'u veya eski tekil entity'ler (geriye uyumluluk)
            if (child.userData?.isDxfRoot || child.userData?.source === 'dxf' ||
                child.userData?.isSelectable || child.userData?.layerName) {
              toRemove.push(child);
            }
          });
          toRemove.forEach(obj => {
            // Group ise tüm alt nesneleri de dispose et
            obj.traverse(child => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
                else child.material.dispose();
              }
            });
            threeState.scene.remove(obj);
          });
        }
        alreadyDrawn.current = false;
      }
      prevDxfLoadIdRef.current = dxfLoadId;
    }
  }, [dxfLoadId, threeState?.scene]);

  // DXF entity cizimi + viewport fit
  useEffect(() => {
    if (threeState?.scene && dxfEntities.length > 0 && !alreadyDrawn.current) {
      // [SAVE-LOAD-REWRITE] TÜM DXF entity'leri SADECE mimari'ye yükle.
      // Eski mantık: _tabId='strong_current'/'weak_current' etiketli entity'leri
      // ilgili disiplin scene'ine yüklüyordu. AMA mimari çöp olarak elektrik
      // paftasına 489 wire sızıyordu (yanlış _tabId etiketleri kayıttan).
      // Kullanıcının elektrik paftada çizdiği wire'lar Redux wires slice'ında
      // saklı → useWireRebuild onları gerçek mesh olarak restore eder.
      // DXF entity'leri yalnızca mimari paftada görsel referans için kullanılır.
      if (!scenesRef.current['mimari']) scenesRef.current['mimari'] = new THREE.Scene();
      // Tüm DXF entity'lerini TEK bir group'a topla.
      // Böylece kullanıcı kat/vaziyet planı seçiminde tüm DXF'i 1 obje olarak seçer
      // (3700+ tekil entity yerine). createFloorPlan / createSitePlan tek clone ile işler.
      const dxfGroup = dxfDrawCore(scenesRef.current['mimari'], dxfEntities, dxfBlocks, "group", allLayers);
      dxfGroup.userData.id = 'dxf_root_' + dxfLoadId;
      dxfGroup.userData.isDxfRoot = true;
      dxfGroup.userData.isSelectable = true;
      dxfGroup.userData.source = 'dxf';
      scenesRef.current['mimari'].add(dxfGroup);

      alreadyDrawn.current = true;

      // Ilk yukleme: viewport'a fit et
      const camera = threeState.camera;
      const controls = controlsRef.current;
      const container = canvasRef.current;
      if (camera && controls && container) {
        const box = computeSafeSceneBox(threeState.scene);
        if (!box.isEmpty()) {
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          camera.position.set(center.x, center.y, 10);
          controls.target.set(center.x, center.y, 0);
          const aspect = container.clientWidth / container.clientHeight;
          const contentAspect = size.x / (size.y || 1);
          const fitZoom = (contentAspect > aspect)
            ? container.clientWidth / size.x
            : container.clientHeight / size.y;
          camera.zoom = fitZoom * 1.1;
          camera.updateProjectionMatrix();
        }
      }
      dispatch(setLoadingMessage(''));
    }
    if (dxfEntities.length === 0) alreadyDrawn.current = false;
  }, [threeState?.scene, dxfEntities, dxfBlocks, allLayers]);

  // Proje yuklendiginde viewport'a fit et
  useEffect(() => {
    const wasLoading = prevProjectLoadingRef.current;
    prevProjectLoadingRef.current = projectIsLoading;
    if (!wasLoading || projectIsLoading) return;
    if (!threeState?.scene) return;
    const camera = threeState.camera;
    const controls = controlsRef.current;
    const container = canvasRef.current;
    if (!camera || !controls || !container) return;
    const tid = setTimeout(() => {
      const targetScene = threeState.scene;
      const box = computeSafeSceneBox(targetScene);
      if (box.isEmpty()) return;
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      camera.position.set(center.x, center.y, 10);
      controls.target.set(center.x, center.y, 0);
      const aspect = container.clientWidth / container.clientHeight;
      const contentAspect = size.x / (size.y || 1);
      const fitZoom = (contentAspect > aspect)
        ? container.clientWidth / size.x
        : container.clientHeight / size.y;
      camera.zoom = fitZoom * 0.85;
      camera.updateProjectionMatrix();
    }, 200);
    return () => clearTimeout(tid);
  }, [projectIsLoading, threeState]);

  return { dxfEntities };
}
